// @flow
import moment from 'moment'
import appendWalkSteps from './appendWalkSteps'
import searchPriceBuilder from './tripPrice'
import searchStepBuilder from './searchStep'
import buildLocation from './searchLocation'
import { TRIP_TYPE } from '../../../constants/tripType'
import { isCancelationFree, getAccurateTime } from '../../../models/step'

// Types
import type { Trip, Tag } from '../../../models/trip'
import type { Step } from '../../../models/step'
import type { SearchResponse, BaseSearchTrip } from '../../../api_types'

type AllSteps = {
  steps: Array<Step>,
  returnSteps: Array<Step>,
}

/**
 * Returns discount percentage from trip tags
 * @param  {Array} tags Trip tags
 * @return {string|null} Discount percentage or null if does not exist
 * @private
 */
export function buildDiscountPercentage(tags: ?Array<Tag>): string | null {
  if (!tags) return null
  const discountTag = tags.find((tag) => tag.tag_type === 'discount')
  return (discountTag && discountTag.text) || null
}

/**
 * By given trip and response returns built step objects
 * with unified interface
 * @param  {Object} trip     The trip object
 * @param  {Object} response The whole response object
 * @return {Object}
 * @private
 */
export function buildSearchSteps(
  trip: BaseSearchTrip,
  response: SearchResponse
): AllSteps {
  const oneWaySteps = (trip.steps || []).map((x) =>
    searchStepBuilder(x, trip, response, false)
  )

  const returnSteps = (trip.return_steps || []).map((x) =>
    searchStepBuilder(x, trip, response, true)
  )

  return {
    steps: appendWalkSteps(oneWaySteps),
    returnSteps: appendWalkSteps(returnSteps),
  }
}

/**
 * Build tags from tags from search response.
 * @param  {BaseSearchTrip} trip
 * @param  {SearchResponse} response
 * @return {Array<Tag>}
 */
export function buildTripTags(
  trip: BaseSearchTrip,
  response: SearchResponse
): Array<Tag> {
  const baseTags = (trip.tags || []).slice()

  if (trip.type === TRIP_TYPE.ON_DEMAND) {
    const mobileCategories = response.mobile_categories || []
    const mobileCategory = trip.vehicle && trip.vehicle.mobile_category
    const categoryObj = mobileCategories.find((c) => c.id === mobileCategory)

    if (categoryObj && categoryObj.name) {
      baseTags.push({
        text: categoryObj.name,
        tag_type: 'mobile_category',
        priority: 5,
      })
    }
  }

  return baseTags
}

/**
 * By given trip and response object return unified trip object
 * @param {0bject} trip The trip object
 * @param {Object} response The whole search response object
 * @return {Object}
 * @private
 */
export function buildOnesearchTrip(
  trip: BaseSearchTrip,
  response: SearchResponse
): Trip {
  const parsedPrice = searchPriceBuilder(
    {
      price: trip.price,
      cost: !trip.cost
        ? undefined
        : {
            base_fare: trip.cost.base_fare,
            waiting_minute: trip.cost.waiting_minute,
          },
      estimates: trip.estimate,
    },
    response.currency
  )

  const stepsObj = buildSearchSteps(trip, response)
  const allSteps = [...stepsObj.steps, ...stepsObj.returnSteps]
  const startLocation = response.start_location
    ? buildLocation(response.start_location)
    : null
  const endLocation = response.end_location
    ? buildLocation(response.end_location)
    : null

  return {
    id: trip.id,
    type: trip.type,
    searchId: response.id,
    price: parsedPrice,
    totalPrice: parsedPrice.raw,
    steps: stepsObj.steps,
    returnSteps: stepsObj.returnSteps,
    duration: getAccurateTime(allSteps),
    recommended: false,
    fltSupport: !!trip.flt_support,
    discountPercentage: buildDiscountPercentage(trip.tags),
    freeCancellation: isCancelationFree(allSteps),
    isEstimate: false,
    rewardPoints: trip.loyalty?.points || 0,
    rewardProgram: trip.loyalty?.program,
    rewardProgramDisplay: trip.loyalty?.display_name, // eslint-disable-line camelcase
    tags: buildTripTags(trip, response),
    createdAt: moment(),
    expiresAt: response.expires_at,
    expiresIn: response.expires_in,
    flightType: response.flight_type,
    numPassengers: response.num_passengers,
    flightDatetime: response.flight_datetime
      ? moment.parseZone(response.flight_datetime)
      : null,
    returnFlightDatetime: response.return_flight_datetime
      ? moment.parseZone(response.return_flight_datetime)
      : null,
    pickupDatetime: response.pickup_datetime
      ? moment.parseZone(response.pickup_datetime)
      : null,
    returnPickupDatetime: response.return_pickup_datetime
      ? moment.parseZone(response.return_pickup_datetime)
      : null,
    startLocation,
    endLocation,

    // missing in v2
    taxesAndFeesIncluded: true,
    outOfPolicy: false,
    vehiclesIncluded: [],
    preferred: false,
    preferredByCompany: false,
  }
}

export default buildOnesearchTrip
